import * as React from "react";
import {
  createUploadJob,
  fetchChildFolders,
  fetchDrives,
  getBackendToken,
  fetchOneDriveChildFolders,
  fetchOneDriveRootFolders,
  fetchRootFolders,
  fetchSites,
  getToken,
} from "../api/sharepointApi";
import type { DestinationPlatform, DriveItem, FolderItem, SiteItem, UploadState } from "../types/sharepoint";
import type { SelectedMailItem } from "./useSelectedMailItems";
import { updateNodeChildren } from "../utils/folderUtils";

interface SharePointState {
  destinationPlatform: DestinationPlatform;

  sites: SiteItem[];
  sitesLoading: boolean;
  sitesError: string;
  selectedSite: SiteItem | undefined;

  drives: DriveItem[];
  drivesLoading: boolean;
  drivesError: string;
  selectedDrive: DriveItem | undefined;

  folders: FolderItem[];
  foldersLoading: boolean;
  foldersError: string;
  selectedFolder: FolderItem | undefined;
  expandedFolders: Record<string, boolean>;
  loadingFolderIds: Record<string, boolean>;
  folderQuery: string;

  upload: UploadState;
  lastCreatedJobId: string;

  selectDestinationPlatform: (platform: DestinationPlatform) => void;
  loadSites: () => Promise<void>;
  loadOneDriveFolders: () => Promise<void>;
  selectSite: (siteId: string) => Promise<void>;
  selectDrive: (driveId: string) => Promise<void>;
  selectFolder: (node: FolderItem) => void;
  toggleFolder: (folderId: string) => void;
  expandFolder: (node: FolderItem) => Promise<void>;
  setFolderQuery: (q: string) => void;
  uploadEmail: (options: UploadOptions, selectedEmails?: SelectedMailItem[]) => Promise<void>;
  clearUploadStatus: () => void;
}

export interface UploadOptions {
  skipDuplicates: boolean;
  includeAttachments: boolean;
  overwrite: boolean;
}

function getCurrentMessageId(item: Office.MessageRead | Office.MessageCompose): string {
  const rawId = (item as { itemId?: string }).itemId;
  if (!rawId) throw new Error("Could not read the current email message ID.");

  if (typeof Office.context.mailbox.convertToRestId === "function") {
    return Office.context.mailbox.convertToRestId(rawId, Office.MailboxEnums.RestVersion.v2_0);
  }

  return rawId;
}

export function useSharePoint(): SharePointState {
  const [destinationPlatform, setDestinationPlatform] =
    React.useState<DestinationPlatform>("sharepoint");

  const [sites, setSites] = React.useState<SiteItem[]>([]);
  const [sitesLoading, setSitesLoading] = React.useState(false);
  const [sitesError, setSitesError] = React.useState("");
  const [selectedSite, setSelectedSite] = React.useState<SiteItem | undefined>();

  const [drives, setDrives] = React.useState<DriveItem[]>([]);
  const [drivesLoading, setDrivesLoading] = React.useState(false);
  const [drivesError, setDrivesError] = React.useState("");
  const [selectedDrive, setSelectedDrive] = React.useState<DriveItem | undefined>();

  const [folders, setFolders] = React.useState<FolderItem[]>([]);
  const [foldersLoading, setFoldersLoading] = React.useState(false);
  const [foldersError, setFoldersError] = React.useState("");
  const [selectedFolder, setSelectedFolder] = React.useState<FolderItem | undefined>();
  const [expandedFolders, setExpandedFolders] = React.useState<Record<string, boolean>>({});
  const [loadingFolderIds, setLoadingFolderIds] = React.useState<Record<string, boolean>>({});
  const [folderQuery, setFolderQuery] = React.useState("");

  const [upload, setUpload] = React.useState<UploadState>({ loading: false, error: "", success: "" });
  const [lastCreatedJobId, setLastCreatedJobId] = React.useState("");

  const clearUploadStatus = () => setUpload({ loading: false, error: "", success: "" });

  const resetDestination = () => {
    setSites([]);
    setSitesError("");
    setSelectedSite(undefined);
    setDrives([]);
    setDrivesError("");
    setSelectedDrive(undefined);
    setFolders([]);
    setFoldersError("");
    setSelectedFolder(undefined);
    setExpandedFolders({});
    setLoadingFolderIds({});
    setFolderQuery("");
    clearUploadStatus();
  };

  async function withLoading<T>(
    setLoading: (v: boolean) => void,
    setError: (v: string) => void,
    fn: (token: string) => Promise<T>
  ): Promise<T | undefined> {
    setLoading(true);
    setError("");
    try {
      const token = await getToken();
      return await fn(token);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      return undefined;
    } finally {
      setLoading(false);
    }
  }

  const selectDestinationPlatform = (platform: DestinationPlatform) => {
    if (platform === destinationPlatform) return;
    setDestinationPlatform(platform);
    resetDestination();
  };

  const loadSites = async () => {
    setDestinationPlatform("sharepoint");
    resetDestination();

    const result = await withLoading(setSitesLoading, setSitesError, fetchSites);
    if (result) setSites(result);
  };

  const loadOneDriveFolders = async () => {
    setDestinationPlatform("onedrive");
    resetDestination();

    const result = await withLoading(setFoldersLoading, setFoldersError, fetchOneDriveRootFolders);
    if (result) {
      setSelectedDrive({ id: "me", name: "OneDrive", driveType: "personal" });
      setFolders(result);
    }
  };

  const selectSite = async (siteId: string) => {
    setSelectedSite(sites.find((s) => s.id === siteId));
    setDrives([]);
    setSelectedDrive(undefined);
    setFolders([]);
    setSelectedFolder(undefined);
    setExpandedFolders({});
    setFolderQuery("");

    const result = await withLoading(setDrivesLoading, setDrivesError, (token) =>
      fetchDrives(siteId, token)
    );
    if (result) setDrives(result);
  };

  const selectDrive = async (driveId: string) => {
    setSelectedDrive(drives.find((d) => d.id === driveId));
    setFolders([]);
    setSelectedFolder(undefined);
    setExpandedFolders({});
    setFolderQuery("");

    const result = await withLoading(setFoldersLoading, setFoldersError, (token) =>
      fetchRootFolders(driveId, token)
    );
    if (result) setFolders(result);
  };

  const selectFolder = (node: FolderItem) => setSelectedFolder(node);

  const toggleFolder = (folderId: string) =>
    setExpandedFolders((prev) => ({ ...prev, [folderId]: !prev[folderId] }));

  const expandFolder = async (node: FolderItem) => {
    if (destinationPlatform === "sharepoint" && !selectedDrive) return;

    setExpandedFolders((prev) => ({ ...prev, [node.id]: true }));
    setLoadingFolderIds((prev) => ({ ...prev, [node.id]: true }));

    try {
      const token = await getToken();
      const children =
        destinationPlatform === "onedrive"
          ? await fetchOneDriveChildFolders(node.id, token)
          : await fetchChildFolders(selectedDrive!.id, node.id, token);

      setFolders((prev) => updateNodeChildren(prev, node.id, children));
    } catch {
      setFolders((prev) => updateNodeChildren(prev, node.id, []));
    } finally {
      setLoadingFolderIds((prev) => {
        const next = { ...prev };
        delete next[node.id];
        return next;
      });
    }
  };

  const uploadEmail = async (options: UploadOptions, selectedEmails: SelectedMailItem[] = []) => {
    if (!selectedFolder || (destinationPlatform === "sharepoint" && (!selectedSite || !selectedDrive))) {
      setUpload({ loading: false, error: "Please select a destination folder first.", success: "" });
      return;
    }

    setUpload({ loading: true, error: "", success: "" });

    try {
      const backendToken = await getBackendToken();
      const graphAccessToken = await getToken();
      const sourceMailboxEmail = Office.context.mailbox.userProfile.emailAddress;
      const emails =
        selectedEmails.length > 0
          ? selectedEmails.map((email) => ({
              messageId: email.messageId,
              ...(email.internetMessageId ? { internetMessageId: email.internetMessageId } : {}),
              subject: email.subject,
            }))
          : (() => {
              const item = Office.context.mailbox.item;
              if (!item) throw new Error("No email is currently open.");

              const subject = (item.subject as string) || "email";
              const messageId = getCurrentMessageId(item);
              const internetMessageId = (item as { internetMessageId?: string }).internetMessageId;

              return [
                {
                  messageId,
                  ...(internetMessageId ? { internetMessageId } : {}),
                  subject,
                },
              ];
            })();

      const destination =
        destinationPlatform === "sharepoint"
          ? {
              platform: "sharepoint" as const,
              siteId: selectedSite!.id,
              driveId: selectedDrive!.id,
              folderId: selectedFolder.id,
            }
          : {
              platform: "onedrive" as const,
              driveId: selectedDrive?.id ?? "me",
              folderId: selectedFolder.id,
            };

      const { jobId } = await createUploadJob(
        {
          sourceMailboxEmail,
          destination,
          skipDuplicates: options.skipDuplicates,
          includeAttachments: options.includeAttachments,
          emails,
        },
        backendToken,
        graphAccessToken
      );
      setLastCreatedJobId(jobId);

      const uploadedLabel =
        emails.length === 1 ? `"${emails[0].subject || "email"}"` : `${emails.length} selected emails`;

      setUpload({
        loading: false,
        error: "",
        success: `Upload job started for ${uploadedLabel}. You can close this pane; progress will continue in Upload Progress.`,
      });
    } catch (e: unknown) {
      setUpload({ loading: false, error: e instanceof Error ? e.message : String(e), success: "" });
    }
  };

  return {
    destinationPlatform,
    sites,
    sitesLoading,
    sitesError,
    selectedSite,
    drives,
    drivesLoading,
    drivesError,
    selectedDrive,
    folders,
    foldersLoading,
    foldersError,
    selectedFolder,
    expandedFolders,
    loadingFolderIds,
    folderQuery,
    upload,
    lastCreatedJobId,
    selectDestinationPlatform,
    loadSites,
    loadOneDriveFolders,
    selectSite,
    selectDrive,
    selectFolder,
    toggleFolder,
    expandFolder,
    setFolderQuery,
    uploadEmail,
    clearUploadStatus,
  };
}
